import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Layout } from '@/components/Layout';
import { ProfileHeader } from '@/components/ProfileHeader';
import { LinkListCard } from '@/components/LinkListCard';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthor } from '@/hooks/useAuthor';
import { useLinkLists } from '@/hooks/useLinkLists';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getKindConfig } from '@/lib/nip51';
import type { LinkList } from '@/hooks/useLinkLists';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const author = useAuthor(pubkey);
  const { data: allLists, isLoading: listsLoading } = useLinkLists(pubkey);
  const { user } = useCurrentUser();
  const isOwner = user?.pubkey === pubkey;

  const displayName = author.data?.metadata?.name ?? nip19.npubEncode(pubkey).slice(0, 12) + '...';

  useSeoMeta({
    title: `${displayName} — Nostr Links`,
    description: author.data?.metadata?.about ?? `Link lists by ${displayName}`,
  });

  // Group lists by kind
  const grouped = (allLists ?? []).reduce<Record<number, LinkList[]>>((acc, list) => {
    if (!acc[list.kind]) acc[list.kind] = [];
    acc[list.kind].push(list);
    return acc;
  }, {});

  const groupedEntries = Object.entries(grouped).map(([kindStr, lists]) => ({
    kind: Number(kindStr),
    config: getKindConfig(Number(kindStr)),
    lists,
  }));

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <ProfileHeader pubkey={pubkey} metadata={author.data?.metadata} isLoading={author.isLoading} />

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Lists</h2>
            {isOwner && (
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/create">
                  <PlusIcon className="size-4" />
                  New List
                </Link>
              </Button>
            )}
          </div>

          {listsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="py-4">
                    <div className="space-y-3">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : groupedEntries.length > 0 ? (
            <div className="space-y-8">
              {groupedEntries.map(({ kind, config, lists }) => (
                <section key={kind}>
                  {/* Section heading — only shown when there are multiple kind groups */}
                  {groupedEntries.length > 1 && config && (
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span>{config.icon}</span>
                      <span>{config.name}s</span>
                    </h3>
                  )}
                  <div className="space-y-4">
                    {lists.map((list) => (
                      <LinkListCard key={list.id} list={list} isOwner={isOwner} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  {isOwner
                    ? "You haven't created any lists yet. Create your first one!"
                    : 'This user hasn\'t created any lists yet.'}
                </p>
                {isOwner && (
                  <Button asChild className="mt-4 gap-1.5">
                    <Link to="/create">
                      <PlusIcon className="size-4" />
                      Create Your First List
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
